import { useEnumHelpers } from '@/queries/useEnumHelpers';
import { AreaEnum } from '@/queries/useEnums';
import { NumericFormat } from 'react-number-format';
import {
  Alert,
  Box,
  Button,
  Collapse,
  IconButton,
  InputAdornment,
  Menu,
  MenuItem,
  TextField,
  Typography,
} from '@mui/material';
import React, { useState } from 'react';
import { FieldErrors, FieldValues, UseFormRegister } from 'react-hook-form';
import { SpaceUsageCard } from './SpaceUsageCard.tsx';

// Define the expected structure for an area unit.
// interface AreaUnit {
//   name: string;
//   value?: string;
//   symbol?: string;
//   unitPerM2: number;
// }

interface SpaceUsageTileProps {
  /**
   * Creates unique-ness across the useRef forms...
   */
  keyId: string;
  usage: string;
  icon: React.ReactNode;
  hideUnit?: boolean;
  value: number;
  unit?: string;
  setUnit?: (unit: string) => void;
  register: UseFormRegister<FieldValues>;
  errors: FieldErrors;
  onChange?: (value: string) => void;
  disabled?: boolean;
}

export const SpaceUsageTile = React.forwardRef<
  HTMLDivElement,
  SpaceUsageTileProps
>(function SpaceUsageRef(
  {
    usage,
    icon,
    hideUnit,
    unit = '',
    value,
    setUnit,
    register,
    errors,
    keyId,
    onChange,
    disabled,
    ...rest
  },
  ref,
) {
  const { areaUnits } = useEnumHelpers();
  // Convert areaUnits to an array of AreaUnit.
  // const areaUnitsArray: AreaUnit[] = Array.isArray(areaUnits)
  //   ? (areaUnits as any[]).map((unit) => ({
  //       name: unit.name,
  //       value: unit.value,
  //       symbol: unit.symbol,
  //       unitPerM2: unit.unitPerM2,
  //     }))
  //   : typeof areaUnits === 'object'
  //     ? Object.values(areaUnits).map((unit: any) => ({
  //         name: unit.name,
  //         value: unit.value,
  //         symbol: unit.symbol,
  //         unitPerM2: unit.unitPerM2,
  //       }))
  //     : [];

  // Fallback area units if API data is not available
  const fallbackAreaUnits: AreaEnum[] = [
    { name: 'SquareMeters', symbol: 'm²', unitPerM2: 1 },
    { name: 'sqft', symbol: 'ft²', unitPerM2: 10.76391042 },
    { name: 'Hectares', symbol: 'ha', unitPerM2: 0.0001 },
    { name: 'Acre', symbol: 'acres', unitPerM2: 0.0002471053815 },
  ];

  const finalAreaUnits = areaUnits.length > 0 ? areaUnits : fallbackAreaUnits;

  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  return (
    <React.Fragment>
      <SpaceUsageCard ref={ref} {...rest}>
        {icon}
        <Collapse in={Boolean(errors[`${keyId}-${usage}`])} timeout={800}>
          <Alert
            severity={
              Boolean(errors[`${keyId}-${usage}`]) ? 'warning' : 'success'
            }
          >
            {errors[`${keyId}-${usage}`]?.message as string}
          </Alert>
        </Collapse>
        <Box sx={{ display: 'flex', alignItems: 'flex-end', width: '100%' }}>
          <NumericFormat
            customInput={TextField}
            thousandSeparator={true}
            autoComplete="off"
            label={usage}
            variant='standard'
            color={usage in errors ? 'error' : 'primary'}
            disabled={disabled}
            value={value ? value : ''}
            onValueChange={(values : any) => {
              const {value} = values;
              onChange?.(value);
            }}
            slotProps={{
              input: {
                endAdornment: (
                  <InputAdornment position={'end'}>
                    <IconButton
                      onClick={(e) => setAnchorEl(e.currentTarget)}
                      color={'secondary'}
                    >
                      <Typography
                        variant={'body2'}
                        sx={{ display: hideUnit ? 'none' : undefined }}
                      >
                        in {unit}
                      </Typography>
                    </IconButton>
                  </InputAdornment>
                ),
              },
              inputLabel: { shrink: true }
            }}
            sx={{ flexGrow: 1 }}
          />
        </Box>
        {/* {!disabled && (
          <Typography variant={'body2'}>
            <strong>OR</strong>
          </Typography>
        )} */}
        {!disabled && (
          <Button
            variant='contained'
            color='success'
            size="small"
            onClick={() => onChange?.('0')}
            sx={{
              fontSize: '0.65rem',
              backgroundColor: 'info.light',
              py: 0.5,
              px: 1,
              alignSelf: 'center',
              minWidth: 'auto',
              borderRadius: 0.5,
              mt: 1,
              mb: 0
            }}
          >
            Not Applicable
          </Button>
        )}
      </SpaceUsageCard>
      <Menu
        open={Boolean(anchorEl)}
        id={'unit-menu'}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        slotProps={{
          list: {
            sx: {
              backgroundColor: 'background.paper',
              '& .MuiMenuItem-root': {
                color: 'text.primary',
                '&:hover': {
                  backgroundColor: 'action.hover',
                },
                '&.Mui-selected': {
                  backgroundColor: 'action.selected',
                  '&:hover': {
                    backgroundColor: 'action.selected',
                  },
                },
              },
            },
          },
        }}
      >
        {finalAreaUnits.length > 0 ? (
          finalAreaUnits.map((u) => (
            <MenuItem
              key={u.name}
              selected={u.name === unit}
              onClick={() => {
                if (!setUnit) {
                  console.error('Unable to set unit, no method supplied');
                  setAnchorEl(null);
                  return;
                }
                setAnchorEl(null);
                setUnit(u.name);
              }}
            >
              {u.symbol || u.name}
            </MenuItem>
          ))
        ) : (
          <MenuItem disabled>Loading units...</MenuItem>
        )}
      </Menu>
    </React.Fragment>
  );
});
